import { useState } from 'react';
import { ArrowRight } from '@phosphor-icons/react';
import { PageHeader } from '../../../components/ui/PageHeader';

interface InterestArea {
  id: string;
  name: string;
  count: number;
  description: string;
  matchingAlumni: string[];
  activeProposals: string[];
}

const INTERESTS: InterestArea[] = [
  {
    id: 'cs-ai',
    name: 'Computer Science & Artificial Intelligence',
    count: 2,
    description: 'Focus on neural networks, scalable systems, generative AI, and algorithms.',
    matchingAlumni: ['Anand Rajaraman (rocketship.vc)', 'Induprakas Keri (Nutanix)'],
    activeProposals: ['Endowment Fund for AI Research Incubator (₹50L)'],
  },
  {
    id: 'civil-infra',
    name: 'Civil Infrastructure & Building Tech',
    count: 2,
    description: 'Modern infrastructure management, building technology, and materials science.',
    matchingAlumni: ['Advait Dhopeshwarkar (Google)', 'Chetan Tolia (Gravitech Advisors)'],
    activeProposals: ['Silver Reunion Scholarship Endowment (₹20L)'],
  },
  {
    id: 'elec-grid',
    name: 'Electrical Engineering & Smart Energy',
    count: 2,
    description: 'Renewable energy integration, smart grids, power transmission, and electronics.',
    matchingAlumni: ['George George (Extra Cover Trading)', 'K. Arvind (State Street)'],
    activeProposals: ['Sustainable Smart Grid Development Lab (₹35L)'],
  },
];

interface AlumniInterestViewProps {
  onBack: () => void;
}

export function AlumniInterestView({ onBack }: AlumniInterestViewProps) {
  const [selectedInterestId, setSelectedInterestId] = useState<string>('cs-ai');
  const selectedInterest = INTERESTS.find((i) => i.id === selectedInterestId) || INTERESTS[0];

  return (
    <div className="space-y-6">
      <PageHeader
        breadcrumb={[
          { label: 'Dashboard', onClick: onBack },
          { label: 'Alumni Interest Matching' },
        ]}
        title="Alumni Interest Matching"
        subtitle="Identify alumni aligned with specific academic, research, or development fields."
      />

      {/* Interest Selector & Info Deck */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Field selector */}
        <div className="bg-white border border-slate-100 rounded-2xl p-5 shadow-sm space-y-3 lg:col-span-1">
          <h3 className="font-outfit font-bold text-[10px] uppercase tracking-widest text-slate-400 mb-3">
            Research & Funding Fields
          </h3>
          <div className="flex flex-col gap-2">
            {INTERESTS.map((interest) => (
              <button
                key={interest.id}
                onClick={() => setSelectedInterestId(interest.id)}
                className={`p-3.5 rounded-xl text-left border transition-smooth ${
                  selectedInterestId === interest.id
                    ? 'bg-accent-light border-accent/30 text-slate-900 font-semibold'
                    : 'bg-white border-slate-100 hover:border-slate-200 text-slate-700'
                }`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm truncate">{interest.name}</span>
                  <span
                    className={`text-xs px-2 py-0.5 rounded-full font-bold shrink-0 ${
                      selectedInterestId === interest.id
                        ? 'bg-accent/15 text-accent border border-accent/20'
                        : 'bg-slate-100 border border-slate-200 text-slate-500'
                    }`}
                  >
                    {interest.count}
                  </span>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Detail Panel */}
        <div className="bg-white border border-slate-100 rounded-2xl p-6 shadow-sm space-y-6 lg:col-span-2">
          <div className="border-b border-slate-100 pb-4">
            <h2 className="font-outfit font-extrabold text-xl text-slate-900">{selectedInterest.name}</h2>
            <p className="text-slate-400 text-sm mt-1">{selectedInterest.description}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Aligned Alumni */}
            <div className="space-y-3">
              <h4 className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                Matching Alumni Contacts
              </h4>
              <div className="space-y-2">
                {selectedInterest.matchingAlumni.map((alum) => (
                  <div key={alum} className="flex items-center gap-2.5 p-3 border border-slate-100 rounded-xl bg-slate-50/50">
                    <div className="w-2.5 h-2.5 rounded-full bg-accent shrink-0" />
                    <span className="text-sm font-semibold text-slate-800">{alum}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Active Proposals */}
            <div className="space-y-3">
              <h4 className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                Active Campaigns / Proposals
              </h4>
              <div className="space-y-2">
                {selectedInterest.activeProposals.map((prop) => (
                  <div key={prop} className="flex items-center gap-2.5 p-3 border border-slate-100 rounded-xl bg-accent-light/50">
                    <div className="w-2.5 h-2.5 rounded-full bg-accent/50 shrink-0" />
                    <span className="text-sm font-semibold text-slate-800">{prop}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="pt-2">
            <button className="flex items-center gap-2 px-5 py-2.5 bg-slate-900 hover:bg-accent text-white text-xs font-bold rounded-xl transition-smooth shadow-sm hover:shadow-md hover:shadow-accent/20">
              Run Complete Alignment Campaign
              <ArrowRight size={13} weight="bold" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
